// src/services/bulkImportService.js
// Bulk upload of experiment rows from a spreadsheet (backend: /data/import/**).
import ApiClient from './apiClient';
import config from '../config';

// Backend errors come back as { message } (GlobalExceptionHandler).
const errorMessage = async (response, fallback) => {
    try {
        const body = await response.json();
        if (body?.message) return body.message;
    } catch (_) { /* non-JSON body */ }
    return fallback;
};

export async function downloadTemplate() {
    const response = await fetch(`${config.apiUrl}/data/import/template`);
    if (!response.ok) throw new Error(await errorMessage(response, 'Could not download the template.'));
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'electrospinning_import_template.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

/** Uploads a file and returns the validation report. Nothing is stored. */
export async function parseImportFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await ApiClient.request('/data/import/parse', { method: 'POST', body: formData });
    if (!response.ok) throw new Error(await errorMessage(response, 'The file could not be read.'));
    return response.json();
}

/**
 * Re-validates (edited) rows: [{ rowNumber, values }], treating `proposals`
 * ([{ kind, name, fullName, casNumber, note }]) as proposed new vocabulary names.
 * `solutions` are the Solutions sheet rows the runs may refer to. Nothing is stored.
 */
export async function validateImportRows(rows, proposals = [], solutions = []) {
    const response = await ApiClient.post('/data/import/validate', { rows, proposals, solutions });
    if (!response.ok) throw new Error(await errorMessage(response, 'The rows could not be checked.'));
    return response.json();
}

/**
 * Stores the rows as one contribution per publication. The uploaded file is
 * sent along so the server keeps it and each record remembers its source row.
 */
export async function commitImport(request, file) {
    const formData = new FormData();
    formData.append('request', new Blob([JSON.stringify(request)], { type: 'application/json' }));
    if (file) formData.append('file', file);
    const response = await ApiClient.request('/data/import/commit', { method: 'POST', body: formData });
    if (!response.ok) throw new Error(await errorMessage(response, 'The data could not be submitted.'));
    return response.json();
}

/**
 * Title and abstract of a DOI from Crossref, or null when it can't be found.
 * Crossref abstracts are JATS XML; tags are stripped.
 */
export async function lookupDoi(doi) {
    try {
        const response = await fetch(`https://api.crossref.org/works/${encodeURIComponent(doi)}`);
        if (!response.ok) return null;
        const { message } = await response.json();
        const title = Array.isArray(message?.title) ? message.title[0] : null;
        const abstract = message?.abstract
            ? message.abstract.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').replace(/^\s*Abstract\s*/i, '').trim()
            : null;
        return title ? { title, abstract } : null;
    } catch (_) {
        return null;
    }
}
